import { Layers, Users } from "lucide-react";

import { Input } from "../ui";
import type { QueueSettingsForm } from "./useQueueSettingsForm";

type Props = {
  form: QueueSettingsForm;
};

export function TeamSection({ form }: Props) {
  const ts = Number(form.teamSize);
  const tc = Number(form.teamCount);
  const isFFA = ts === 1;
  const format = isFFA
    ? `${tc}-player FFA`
    : tc <= 3
      ? Array(tc).fill(ts).join("v")
      : `${ts} × ${tc}`;

  return (
    <div className="space-y-3">
      <p className="text-xs text-surface-500">
        Configure how teams are structured for matches in this queue.
      </p>

      {/* Team Structure */}
      <div className="rounded-lg border border-surface-700/40 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-surface-800/60">
          <div className="flex items-center justify-center size-8 rounded-lg shrink-0 bg-surface-700/50 text-surface-400">
            <Users className="size-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-white">Team Structure</h4>
            <p className="text-2xs text-surface-500 leading-snug">
              Players per team and number of teams per match
            </p>
          </div>
        </div>
        <div className="px-4 pb-4 pt-3 border-t border-surface-700/30 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Team Size"
              type="number"
              value={form.teamSize}
              onChange={(e) => form.setTeamSize(e.target.value)}
              placeholder="e.g. 5"
              min={1}
              max={50}
            />
            <Input
              label="Team Count"
              type="number"
              value={form.teamCount}
              onChange={(e) => form.setTeamCount(e.target.value)}
              placeholder="2"
              min={1}
            />
          </div>
          {form.computedMatchSize > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-700/30 border border-surface-700">
              <span className="text-xs text-surface-400">Format:</span>
              <span className="text-sm font-bold text-white">{format}</span>
              <span className="text-xs text-surface-500">({form.computedMatchSize} players)</span>
            </div>
          )}
        </div>
      </div>

      {/* Queue Options */}
      <div className="rounded-lg border border-surface-700/40 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-surface-800/60">
          <div className="flex items-center justify-center size-8 rounded-lg shrink-0 bg-surface-700/50 text-surface-400">
            <Layers className="size-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-white">Queue Options</h4>
            <p className="text-2xs text-surface-500 leading-snug">
              Additional rules for how players can queue
            </p>
          </div>
        </div>
        <div className="px-4 pb-4 pt-3 border-t border-surface-700/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-surface-300">Allow Full Teams</p>
              <p className="text-xs text-surface-500 mt-0.5">
                Allow pre-made groups that fill an entire team to queue together.
              </p>
            </div>
            <button
              type="button"
              onClick={() => form.setAllowFullTeams((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ml-4 ${
                form.allowFullTeams ? "bg-brand-500" : "bg-surface-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.allowFullTeams ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
